import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Modal, Divider, Form, Select, Spin, Flex, Button, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

const ALL_VALUE = "__ALL__";

const AccountPickerModal = forwardRef(({ onConfirm }, ref) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  useImperativeHandle(ref, () => ({
    open: (currentAccountId) => {
      setVisible(true);
      form.resetFields();
      form.setFieldsValue({
        accountId: currentAccountId ?? ALL_VALUE,
      });
      fetchAccounts();
    },
  }));

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await http.get(api.getAccountDict);
      const list = res?.data || [];
      const mapped = list.map((item) => ({
        value: item.id,
        label: item.employeesName,
        raw: item,
      }));
      setOptions([
        { value: ALL_VALUE, label: "All (Default)", raw: null },
        ...mapped,
      ]);
    } catch (e) {
      message.error("Failed to load employee list");
      setOptions([{ value: ALL_VALUE, label: "All (Default)", raw: null }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.accountId === ALL_VALUE) {
        setVisible(false);
        onConfirm?.(null); // 还原默认态，父组件收到 null 即清空 selectedAccount
        return;
      }
      const record = options.find((o) => o.value === values.accountId)?.raw;
      if (!record) {
        message.warning("Please select an employee");
        return;
      }
      setVisible(false);
      onConfirm?.(record);
    } catch (_err) {
      // 表单校验失败，antd 会自动提示
    }
  };

  return (
    <Modal
      open={visible}
      width={460}
      onCancel={handleClose}
      maskClosable={false}
      keyboard={false}
      title={
        <div style={{ fontSize: "20px", fontWeight: "bold", color: "#0E172B" }}>
          <UserOutlined style={{ marginRight: "8px" }} /> Select Employee
          <Divider style={{ marginTop: 12, marginBottom: 0 }} />
        </div>
      }
      styles={{ body: { minHeight: "160px" } }}
      footer={
        <Flex justify="center" gap={16}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Flex>
      }
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            name="accountId"
            label="Employee"
            rules={[{ required: true, message: "Please select an employee" }]}
          >
            <Select
              placeholder="Search employee name"
              showSearch
              optionFilterProp="label"
              options={options}
              listHeight={220}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
});

export default AccountPickerModal;